import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { OrderStatusConfirmComponent } from '../components/order-status-confirm/order-status-confirm.component';
import { DialogAction, DialogStatus } from '../components/order-status-confirm/order-status-confirm.types';
import { AcceptQuotation, cancelQuotation, deleteQuotation, DuplicateQuotation, validateOrder } from '../store/quotations.actions';

/** Overflow-menu actions an embedded order card can trigger. */
export type OrderSecondaryAction = 'cancel_order' | 'duplicate_order' | 'delete_order';

/**
 * The single main action offered for an order in a given status, mirroring the
 * order-detail page header so a surface embedding an order (today the Smart Inbox
 * timeline artifact) offers the same next step with the same wording.
 */
export interface IOrderPrimaryAction {
  /** Status key the confirmation dialog is built for. */
  transition: 'send_accepted_order' | 'sent' | 'to_be_validated' | 'pending_inventory';
  /** i18n key of the button label. */
  labelKey: string;
  /** Permission the caller must hold for the action to be offered. */
  permission: { module: string; action: string };
}

/**
 * Status → main action map, kept in sync with the `details-quotes` header.
 *
 * A status absent from this map has no main action in the order detail either
 * (`sent_to_erp`, `done`, `sale`, `cancel`, `declined`, `deleted`) — the caller
 * renders the status badge alone.
 */
const PRIMARY_ACTION_BY_STATUS: Record<string, IOrderPrimaryAction> = {
  draft: {
    transition: 'send_accepted_order',
    labelKey: 'quotations_and_orders.create_accepted_order',
    permission: { module: 'order', action: 'approve' },
  },
  requested: {
    transition: 'send_accepted_order',
    labelKey: 'quotations_and_orders.create_accepted_order',
    permission: { module: 'order', action: 'approve' },
  },
  sent: {
    transition: 'sent',
    labelKey: 'quotations_and_orders.accept_and_sign',
    permission: { module: 'order', action: 'approve' },
  },
  to_be_validated: {
    transition: 'to_be_validated',
    labelKey: 'quotations_and_orders.approve_order',
    permission: { module: 'order', action: 'approve' },
  },
  pending_inventory: {
    transition: 'pending_inventory',
    labelKey: 'quotations_and_orders.approve_order',
    permission: { module: 'order', action: 'approve' },
  },
};

/**
 * Drives an order's main status transition from outside the order-detail page.
 *
 * It reuses the same confirmation dialog (`OrderStatusConfirmComponent`), the same
 * i18n keys and the same store actions as `details-quotes`, so approving from the
 * Smart Inbox artifact and approving from the order page are the same operation —
 * including the ERP push that `validateOrder` triggers.
 *
 * NOTE: `details-quotes.confirmAction()` still owns its own copy of these dialog
 * definitions plus the transitions this service does not cover (send quote,
 * cancel, delete, duplicate, archive, ERP-missing follow-ups). Migrating that
 * component onto this service is a separate cleanup — see WB-7401.
 */
@Injectable({ providedIn: 'root' })
export class OrderPrimaryActionService {
  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  /** The main action for a status, or null when the status offers none. */
  getPrimaryAction(status: string | undefined | null): IOrderPrimaryAction | null {
    if (!status) return null;
    return PRIMARY_ACTION_BY_STATUS[status] ?? null;
  }

  /**
   * Open the confirmation dialog for an order's main action and, on confirm,
   * dispatch the transition. Resolves the same way the order-detail page does.
   */
  confirm(action: IOrderPrimaryAction, order: { id: number; status: string }): void {
    const label = (key: string) => this.translate.instant(key);
    const statusLabel = (status: string) =>
      label('quotations_and_orders.QuotationStatus.' + status);

    let title = '';
    let description = '';
    let status: { from?: DialogStatus; to?: DialogStatus } = {};
    let confirmLabel = '';
    let onConfirm: () => void = () => undefined;

    switch (action.transition) {
      case 'send_accepted_order':
        title = label('quotations_and_orders.status_dialog.send_accept_order.title');
        description = label('quotations_and_orders.status_dialog.send_accept_order.description');
        status = {
          from: {
            text: statusLabel(order.status),
            class: order.status === 'draft' ? 'status-draft' : 'status-primary',
          },
          to: { text: statusLabel('done'), class: 'status-success' },
        };
        confirmLabel = label('quotations_and_orders.status_dialog.actions.create_accepted_order');
        onConfirm = () => this.store.dispatch(validateOrder({ id: order.id }));
        break;

      case 'sent':
        title = label('quotations_and_orders.status_dialog.accept_order.title');
        description = label('quotations_and_orders.status_dialog.accept_order.description');
        status = {
          from: { text: statusLabel('sent'), class: 'status-primary' },
          to: { text: statusLabel('to_be_validated'), class: 'status-primary' },
        };
        confirmLabel = label('quotations_and_orders.status_dialog.actions.accept_order');
        onConfirm = () => this.store.dispatch(AcceptQuotation({ id: order.id }));
        break;

      case 'to_be_validated':
      case 'pending_inventory':
        title = label('quotations_and_orders.status_dialog.approve_order.title');
        description = label('quotations_and_orders.status_dialog.approve_order.description');
        status = {
          from: {
            text: statusLabel(action.transition),
            class: action.transition === 'pending_inventory' ? 'status-pending' : 'status-primary',
          },
          to: { text: statusLabel('done'), class: 'status-success' },
        };
        confirmLabel = label('quotations_and_orders.status_dialog.actions.approve_order');
        onConfirm = () => this.store.dispatch(validateOrder({ id: order.id }));
        break;
    }

    const actions: DialogAction[] = [
      {
        label: label('quotations_and_orders.status_dialog.actions.cancel'),
        class: 'action-danger',
        callback: () => this.dialog.closeAll(),
      },
      {
        label: confirmLabel,
        class: 'action-primary',
        icon: { position: 'after', name: 'arrow_forward' },
        callback: () => {
          this.dialog.closeAll();
          onConfirm();
        },
      },
    ];

    this.dialog.open(OrderStatusConfirmComponent, {
      data: { title, description, status, actions, misProducts: [], misInventory: [] },
    });
  }

  /**
   * Destructive / housekeeping actions offered in an order's overflow menu, using
   * the same dialog copy and store actions as the order-detail page.
   */
  confirmSecondary(action: OrderSecondaryAction, order: { id: number; name?: string }): void {
    const label = (key: string, params?: object) => this.translate.instant(key, params);

    let title = '';
    let description = '';
    let mainIcon = '';
    let confirmLabel = '';
    let confirmClass = 'action-primary';
    let confirmIcon: DialogAction['icon'];
    let onConfirm: () => void = () => undefined;

    switch (action) {
      case 'cancel_order':
        title = label('quotations_and_orders.status_dialog.cancel_order.title');
        description = label('quotations_and_orders.status_dialog.cancel_order.description');
        mainIcon = 'assets/icones/main_cancel_icon.svg';
        confirmLabel = label('quotations_and_orders.status_dialog.actions.cancel_order');
        confirmClass = 'action-cancel';
        confirmIcon = { position: 'before', name: 'assets/icones/cancel_icon_white.svg' };
        onConfirm = () => this.store.dispatch(cancelQuotation({ id: order.id }));
        break;

      case 'duplicate_order':
        title = label('quotations_and_orders.confirm.duplicate_title');
        description = label('quotations_and_orders.confirm.duplicate_body', { name: order.name ?? '' });
        mainIcon = 'assets/icones/copy.svg';
        confirmLabel = label('quotations_and_orders.actions.duplicate');
        confirmIcon = { position: 'after', name: 'arrow_forward' };
        onConfirm = () => this.store.dispatch(DuplicateQuotation({ id: order.id }));
        break;

      case 'delete_order':
        title = label('quotations_and_orders.status_dialog.delete_order.title');
        description = label('quotations_and_orders.status_dialog.delete_order.description');
        mainIcon = 'assets/icones/main_delete_icon.svg';
        confirmLabel = label('quotations_and_orders.status_dialog.actions.delete_order');
        confirmClass = 'action-cancel';
        confirmIcon = { position: 'before', name: 'assets/icones/cancel_icon_white.svg' };
        onConfirm = () => this.store.dispatch(deleteQuotation({ id: order.id, confirm: true }));
        break;
    }

    const actions: DialogAction[] = [
      {
        label: label('quotations_and_orders.status_dialog.actions.go_back'),
        class: 'action-light',
        callback: () => this.dialog.closeAll(),
      },
      {
        label: confirmLabel,
        class: confirmClass,
        icon: confirmIcon,
        callback: () => {
          this.dialog.closeAll();
          onConfirm();
        },
      },
    ];

    this.dialog.open(OrderStatusConfirmComponent, {
      data: { title, description, mainIcon, actions, misProducts: [], misInventory: [] },
    });
  }

  /**
   * Whether an order may still be cancelled / deleted. Mirrors
   * `details-quotes.canShowDeleteAction()` and the status group that exposes the
   * Cancel button there.
   */
  canCancel(status: string | undefined | null): boolean {
    return !!status && ['to_be_validated', 'pending_inventory'].includes(status);
  }

  canDelete(status: string | undefined | null): boolean {
    return !!status && !['done', 'sent_to_erp'].includes(status);
  }
}
